import React, { useEffect, useRef, useState } from "react";
import { MdOutlineKeyboardArrowDown } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import currencies from "../../../assets/store/currencies";
import colors from "../../../theme/color/ColorTheme";
import fontSize from "../../../theme/fonts/FontTheme";
import LogoDefaultText from "../../logo/LogoDefaultText";
import LogoFull from "../../logo/LogoFull";
import ClothingMegaMenu from "../../megaMenu/ClothingMegaMenu";
import ScarvesMegaMenu from "../../megaMenu/ScarvesMegaMenu";
import SideMenuAccountSignIn from "../../section/account/SideMenuAccountSignIn";
import SideMenuAccountSignup from "../../section/account/SideMenuAccountSignup";
import SidemenuCart from "../../section/cart/SidemenuCart";
import SidemenuCartEdit from "../../section/cart/SidemenuCartEdit";
import SearchOverlay from "../../section/search/SearchOverlay";

const Wrapper = styled.div`
  width: 100%;
`;

const Container = styled.div`
  height: 100px;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  padding: 0 40px;

  border-bottom: ${({ $isScrolled, $isMegaOpen }) =>
    $isScrolled || $isMegaOpen
      ? `1px solid ${colors.line}`
      : "1px solid transparent"};

  background-color: ${({ $isScrolled, $isMegaOpen, $isMenuOpen }) =>
    $isScrolled || $isMegaOpen || $isMenuOpen ? "#ffffff" : "transparent"};
  transition: background-color 0.6s ease, border 0.6s ease;

  &:hover {
    ${({ $isScrolled }) =>
      !$isScrolled &&
      `
      background-color: #fff;
     border-bottom: 1px solid ${colors.line};
    `}
  }
`;

const NavList = styled.ul`
  display: flex;
  align-items: center;
  gap: 24px;
  padding: 0;
  margin: 0;
  list-style: none;
`;

const NavItem = styled.li`
  font-size: ${fontSize.xxxs};
  letter-spacing: 0.08rem;
  cursor: pointer;
  position: relative;
  color: #000;

  &::after {
    content: "";
    position: absolute;
    left: 50%;
    bottom: -4px;
    width: 100%;
    height: 1px;
    background-color: black;
    transform: translateX(-50%) scaleX(0);
    transform-origin: center;
    transition: transform 0.3s ease;
  }

  &:hover::after {
    transform: translateX(-50%) scaleX(1);
  }
`;

const Left = styled.nav`
  justify-self: start;
`;

const Center = styled.div`
  justify-self: center;
`;

const Right = styled.nav`
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 24px;
`;

const CurrencySelectWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

const CurrencySelect = styled.select`
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: none;
  background: transparent;
  padding-right: 16px;
  text-align: right;
  font-size: ${fontSize.xxxs};
  letter-spacing: 0.08rem;
  cursor: pointer;
  outline: none;
  direction: rtl;

  option {
    background: white;
    color: black;
  }
`;

const Icon = styled(MdOutlineKeyboardArrowDown)`
  position: absolute;
  right: 0;
  pointer-events: none;
  color: ${colors.black};
  height: 16px;
  width: 16px;
`;

const appear = keyframes`
  0% {
    opacity: 0;
    transform: translateY(-5px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const MegaMenu = styled.div`
  height: auto;
  width: 100%;
  background-color: ${colors.white};
  animation: ${appear} 0.3s ease;
  box-shadow: rgba(0, 0, 0, 0.25) 0px 25px 50px -12px;
`;

const MEGA_MENU_MAP = {
  CLOTHING: ClothingMegaMenu,
  SCARVES: ScarvesMegaMenu,
};

function DesktopNavbar() {
  const navigate = useNavigate();
  const [isScrolled, setIsScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [selectedCurrency, setSelectedCurrency] = useState(currencies[0].abbr);

  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [menuType, setMenuType] = useState();
  const hideTimeout = useRef(null);

  const handleMouseEnter = (type) => {
    clearTimeout(hideTimeout.current);
    setMenuType(type);
    setShowMegaMenu(true);
  };

  const handleMouseLeave = () => {
    hideTimeout.current = setTimeout(() => {
      setShowMegaMenu(false);
    }, 100);
  };

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const toggleMenu = (menu) =>
    setOpenMenu((prev) => (prev === menu ? null : menu));

  return (
    <Wrapper>
      <Container
        $isScrolled={isScrolled}
        $isMegaOpen={showMegaMenu}
        $isMenuOpen={openMenu}
      >
        <Left>
          <NavList>
            <NavItem
              // onMouseEnter={() => handleMouseEnter("SCARVES")}
              // onMouseLeave={handleMouseLeave}

              onClick={() => (window.location.href = `/browse?category=shawl`)}
            >
              SCARVES
            </NavItem>

            <NavItem onClick={() => navigate("/")}>HOME</NavItem>
            <NavItem onClick={() => navigate("/verify-ownership")}>
              OWNERSHIP
            </NavItem>
            <NavItem
              onMouseEnter={() => handleMouseEnter("CLOTHING")}
              onMouseLeave={handleMouseLeave}
            >
              <span onClick={() => navigate("/browse")}>CLOTHING</span>
            </NavItem>

            <NavItem onClick={() => navigate("/journal")}>JOURNAL</NavItem>
            <NavItem onClick={() => navigate("/founder")}>ABOUT US</NavItem>
          </NavList>
        </Left>

        <Center onClick={() => navigate("/")}>
          {isScrolled ? <LogoFull /> : <LogoDefaultText />}
        </Center>

        <Right>
          <CurrencySelectWrapper>
            <CurrencySelect
              value={selectedCurrency}
              onChange={(e) => setSelectedCurrency(e.target.value)}
            >
              {currencies.map((c) => (
                <option key={c.abbr} value={c.abbr}>
                  {c.abbr}
                </option>
              ))}
            </CurrencySelect>
            <Icon />
          </CurrencySelectWrapper>
          <NavList>
            <NavItem onClick={() => toggleMenu("signin")}>ACCOUNT</NavItem>
            <NavItem onClick={() => navigate("/contact")}>CONTACT</NavItem>
            <NavItem onClick={() => navigate("/dashboard")}>WISHLIST</NavItem>
            <NavItem onClick={() => toggleMenu("cart")}>CART</NavItem>
          </NavList>
        </Right>
      </Container>

      {openMenu === "search" && (
        <SearchOverlay onClose={() => setOpenMenu(null)} />
      )}
      {openMenu === "cart" && (
        <SidemenuCart
          onClose={() => setOpenMenu(null)}
          onOpenEdit={() => setOpenMenu("cartEdit")}
        />
      )}

      {openMenu === "cartEdit" && (
        <SidemenuCartEdit onClose={() => setOpenMenu("cart")} />
      )}

      {openMenu === "signin" && (
        <SideMenuAccountSignIn
          onClose={() => setOpenMenu(null)}
          openSignup={() => setOpenMenu("signup")}
        />
      )}
      {openMenu === "signup" && (
        <SideMenuAccountSignup
          onClose={() => setOpenMenu(null)}
          openSignin={() => setOpenMenu("signin")}
        />
      )}

      {showMegaMenu && menuType && MEGA_MENU_MAP[menuType] && (
        <MegaMenu
          onMouseEnter={() => clearTimeout(hideTimeout.current)}
          onMouseLeave={handleMouseLeave}
        >
          {React.createElement(MEGA_MENU_MAP[menuType])}
        </MegaMenu>
      )}
    </Wrapper>
  );
}

export default DesktopNavbar;
